import { TrustAccountStatus, UnrestrictedAccountStatus } from '../../types';

export interface FirmBankingSystemAccountData {
  accountNumber: string;
  routingNumber: string;
  balance: string;
  projectedBalance: string;
  status: string;
  withdrawalAvailable: boolean;
  sweep?: {
    status: keyof typeof SWEEP_STATUSES,
    accountNumber: string | null,
  }
}

export type IntegrationProvisioningData = {
  ofac?: {
    status: string;
    reason: string | null;
  };
  provisioning: {
    error?: {
      status: string;
      reason: string | null;
    };
    status: keyof typeof OFAC_STATUSES | keyof typeof INTEGRATION_STATUS;
  };
};

export interface FirmBankingData {
  accounts: {
    case: FirmBankingSystemAccountData | null;
    rev: FirmBankingSystemAccountData | null;
    exp: FirmBankingSystemAccountData | null;
  };
  canStartIntegration: boolean;
}

export enum INTEGRATION_STATUS {
  InProgress = 'In Progress',
  Error = 'Error',
  CriticalError = 'Critical Error',
  Completed = 'Completed',
}

export enum OFAC_STATUSES {
  PENDING_VERIFICATION = 'Pending Verification',
  VERIFIED = 'Verified',
  UNDER_REVIEW = 'Under Review',
  REJECTED = 'Rejected',
  IGNORED = 'Ignored',
}

export enum SYSTEM_ACCOUNT_LABELS {
  REV = 'Revenue Recognition',
  EXP = 'Expense Reimbursement',
  case = 'Case Clearing',
}

export enum SYSTEM_ACCOUNT_STATUSES {
  INACTIVE = 'INACTIVE',
  ACTIVE = 'ACTIVE',
  BLOCKED = 'BLOCKED',
  CLOSURE_INITIATED = 'CLOSURE_INITIATED',
  CLOSED = 'CLOSED',
}

export enum SWEEP_STATUSES {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  NEEDS_ATTENTION = 'NEEDS_ATTENTION',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export interface UnrestrictedAccountSubmitFormProps {
  name: string;
  type: string;
  accountNumber: string;
  routingNumber: string;
}

export interface UnrestrictedAccountTableItem {
  createdAt?: string;
  id: number;
  name: string;
  type: string;
  accountNumber: string;
  routingNumber: string;
  isEwsVerified: string;
  status: UnrestrictedAccountStatus;
  canBeDeleted: boolean;
  updatedAt: string;
}

export interface TrustAccountSubmitFormProps {
  name: string;
  type: string;
  accountNumber: string;
  routingNumber: string;
}

export interface TrustAccountTableItem {
  createdAt?: string;
  id: number;
  name: string;
  type: string;
  accountNumber: string;
  routingNumber: string;
  isEwsVerified: string;
  status: TrustAccountStatus | string;
  canBeDeleted: boolean;
  updatedAt: string;
}

export interface FirmBankingWithdrawFormSubmitProps {
  fromSystemAccountType: string;
  toUnrestrictedAccountId: string;
  amount: string;
}

export type AutoSweepTriggerMode = 'SCHEDULED' | 'IMMEDIATE';

export type AutoSweepAmountType = 'FULL';

export interface AutoSweepSchedule {
  dayOfWeek: number; // 0 = Sunday, 6 = Saturday
  hour: number;
  minute: number;
  timezone: string;
}

export interface AutoSweepTrigger {
  mode: AutoSweepTriggerMode;
  schedule?: AutoSweepSchedule;
}

export interface AutoSweepAmount {
  type: AutoSweepAmountType;
}

export interface AutoSweepConfig {
  enabled?: boolean;
  destinationAccountId?: number;
  trigger?: AutoSweepTrigger;
  amount?: AutoSweepAmount;
  retryDelayMinutes?: number;
  maxRetries?: number;
  lastSweep?: string;
}

export interface AutoSweepConfigPayload {
  revSweep: AutoSweepConfig;
  expSweep: AutoSweepConfig;
}

// export interface AutoSweepConfig {
//   enabled?: boolean;
//   destinationAccountId?: number;
//   trigger?: AutoSweepTrigger;
//   amount?: AutoSweepAmount;
//   retryDelayMinutes?: number;
//   maxRetries?: number;
// }


export interface AutoSweepTransactionListResultSet {
  items: AutoSweepTransaction[];
  total: number;
  page: number;
  size: number;
}

export interface AutoSweepTransaction {
  id: number;
  batchCode: string | null;
  accountType: AutoSweepAccountType;
  status: AutoSweepStatus;
  amount: number;
  attyFeeId: number | null;
  deductionId: number | null;
  createdAt: string;
  scheduledAt: string | null;
  completedAt: string | null;
  updatedAt: string | null;
  passportTransactionId: number | null;
  retryCount: number;
  destinationAccountName?: string;
  caseReferenceId?: string | null;
  plaintiffName?: string | null;
}

export interface AutoSweepTransactionDetails {
  id: number;
  accountType: AutoSweepAccountType;
  status: AutoSweepStatus;
  amount: number;
  attyFeeId: number | null;
  deductionId: number | null;
  createdAt: string;
  updatedAt?: string;
  firmId?: number;
  destinationAccountId?: number | null;
  idempotencyKey?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  scheduledAt: string | null;
  completedAt: string | null;
  passportTransactionId: number | null;
  retryCount: number;
}

export interface AutoSweepTransactionListParams {
  page?: number;
  size?: number;
  accountType?: AutoSweepAccountType;
  status?: AutoSweepStatus;
  firmId: number;
}

export interface AutoSweepTransactionDetailsResultSet
  extends AutoSweepTransaction {
  firmId: number;
  destinationAccountId: number;
  idempotencyKey: string;
  errorCode: string | null;
  errorMessage: string | null;
  updatedAt: string;
}

export type AutoSweepAccountType = 'REV' | 'EXP';

export type AutoSweepDayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type AutoSweepTimezone =
  | 'America/New_York'
  | 'America/Chicago'
  | 'America/Denver'
  | 'America/Los_Angeles'
  | 'America/Phoenix'
  | 'America/Toronto'
  | 'America/Vancouver'
  | 'Europe/London'
  | 'Europe/Paris'
  | 'Europe/Berlin'
  | 'Europe/Rome'
  | 'Europe/Moscow'
  | 'Asia/Tokyo'
  | 'Asia/Shanghai'
  | 'Asia/Singapore'
  | 'Asia/Kolkata'
  | 'Asia/Dubai'
  | 'Australia/Sydney'
  | 'Australia/Melbourne'
  | 'Australia/Perth';

export type AutoSweepStatus =
  | 'Queued'
  | 'Pending'
  | 'Processing'
  | 'Completed'
  | 'Failed'
  | 'RetryScheduled'
  | 'RetryFailed'
  | 'Cancelled';

export enum AccountTypeShortCuts {
  'Revenue Recognition' = 'REV',
  'Expense Reimbursement' = 'EXP',
}

export enum MenuOption {
  Details = 'See Details',
  Edit = 'Edit',
}

export interface AutoSweepBatchDetailsResultSet {
  batchCode: string;
  accountType: AutoSweepAccountType;
  amount: number;
  status: AutoSweepStatus;
  updatedAt: string;
  scheduledAt: string | null;
  completedAt: string | null;
  contributions: Array<{
    amount: number;
    settlementId: number;
    attyFeeId: number | null;
    deductionId: number | null;
    caseReferenceId: string | null;
    plaintiffName: string | null;
  }>;
  total: number;
  page: number;
  size: number;
}